import { getErrorMessage } from '@/components/hooks/useGetErrorMessage';
import { useHandleActionItem } from '@/components/hooks/useHandleActionItem';
import { useCoreContext } from '@/core';
import { PropsBusinessUnitForm } from '@/domains/business-unit/entities';
import { BusinessUnitPayloadForm } from '@/domains/business-unit/ports/payloads';
import {
  CreateBusinessUnitUsecase,
  UpdateBusinessUnitUsecase,
} from '@/domains/business-unit/usecases';
import { yupResolver } from '@hookform/resolvers/yup';
import { notification } from 'antd';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BusinessUnitHttpRepository } from '../adapters/repositories/business-unit.http.repository';
import { formatDataEdit } from '../mapper/formatDataEdit';
import { validateBusinessUnit } from '../validations';

const useBusinessUnitForm = (props: PropsBusinessUnitForm) => {
  const { detailBusinessUnit } = props;
  const { t } = useTranslation();
  const { handleClose } = useHandleActionItem();
  const { actionGetListBU, actionGetAllBU, getValues } = useCoreContext();
  const [currentQueryParameters] = useSearchParams();
  const showId = currentQueryParameters.get('id');
  const createBusinessUnit = new CreateBusinessUnitUsecase(new BusinessUnitHttpRepository());
  const updateBusinessUnit = new UpdateBusinessUnitUsecase(new BusinessUnitHttpRepository());
  const { schemaBusinessUnit } = validateBusinessUnit(props);

  const initialValue: BusinessUnitPayloadForm = {
    id: '',
    name: '',
    status: 'ACTIVE',
    parentId: '',
    businessDomainId: '',
    childrenIds: [],
    categoryIds: [],
    offeringProductGroupIds: [],
  };

  const methodForm = useForm<BusinessUnitPayloadForm>({
    mode: 'onTouched',
    defaultValues: {
      id: detailBusinessUnit.id ?? '',
      name: detailBusinessUnit.name ?? '',
      status: detailBusinessUnit.status ?? 'ACTIVE',
      parentId: detailBusinessUnit.parentId ?? '',
      businessDomainId: detailBusinessUnit.businessDomainId ?? '',
      childrenIds: detailBusinessUnit.childrenIds ?? [],
      categoryIds: detailBusinessUnit.categoryIds ?? [],
      offeringProductGroupIds: detailBusinessUnit.offeringProductGroupIds ?? [],
    },
    resolver: yupResolver(schemaBusinessUnit),
  });

  const refreshList = () => {
    actionGetListBU(getValues());
    actionGetAllBU();
  };

  const onSubmit = methodForm.handleSubmit(async (data: BusinessUnitPayloadForm) => {
    const payload: BusinessUnitPayloadForm = {
      ...data,
      parentId: data.parentId || undefined,
      childrenIds: data.childrenIds ?? [],
      categoryIds: data.categoryIds ?? [],
      offeringProductGroupIds: data.offeringProductGroupIds ?? [],
    };

    try {
      if (!showId) {
        delete payload.id;
        await createBusinessUnit.run(payload);
        notification.success({
          message: t('common.businessUnitCreatedSuccess'),
          placement: 'bottomRight',
        });
        handleClose();
        methodForm.reset({ ...initialValue });
        refreshList();
      } else {
        await formatDataEdit({
          data: payload,
          detail: detailBusinessUnit,
          handleClose,
          methodForm,
          initialValue,
          actionRefreshList: refreshList,
          update: updateBusinessUnit,
          t,
        });
      }
    } catch (error) {
      notification.error({
        message: t('common.error'),
        description: getErrorMessage(error),
        placement: 'bottomRight',
      });
    }
  });

  return {
    methodForm,
    onSubmit,
    initialValue,
  };
};

export default useBusinessUnitForm;
